// Sign in the Assertion terminal agent or Mac app.
//
// Two kinds of link open this page:
//  - /device?port=…&state=…&challenge=…&device=…  (current clients). One click on Connect: we ask
//    the memory backend for a one-time grant and send the browser to the app's own callback on
//    127.0.0.1, which redeems it. There is no code to compare — see utils/appSignin.js.
//  - /device?code=XXXX-XXXX  (clients from before the callback, and machines with no browser). The
//    user confirms the code the app shows and approves.
//  - /device?connected=1&device=…  is where the app's callback sends the browser once it is in.
//  - /device?app=ios&return=https&…  newer iPhone builds. No Studio screens at all: signed out, we go
//    straight to Microsoft's page and come back here; signed in, we grant at once and send the
//    browser to https://<this origin>/app/signin, which only our app can receive (its
//    ASWebAuthenticationSession claims it through apple-app-site-association's webcredentials). The
//    Connect click exists to stop a grant reaching another app that registered our custom scheme;
//    with that ruled out, and the grant useless without the verifier on the phone, it adds nothing.
//    app.js lets this variant past RequireAuth's /login so the redirect happens here.
//
// RequireAuth only knows that MSAL remembers an account; that account can outlive the session
// behind it. So before offering anything we check that a token can actually be had, and if not
// we offer a real sign-in that comes back to this same link — a dead session used to leave the
// user on the code form with "INTERACTION_REQUIRED" and no way forward.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Box, Button, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Logo from "../assets/icons/logo_full.png";
import { approveDevice, canCallApiSilently, grantAppSignIn } from "../utils/api";
import { callbackUrl, parseAppSignin } from "../utils/appSignin";
import { authReturnFailed, savePostAuthRedirect } from "../utils/authRedirect";
import { loginRequest, b2cPolicyName, forgetCachedAccount } from "../authConfig";

const normalize = (v) => {
  const raw = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  return raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
};

const accountEmail = (a) => a?.idTokenClaims?.emails?.[0] || a?.username || "";

// The phone's https sign-in redirects to Microsoft on its own, so it must not do it twice for one
// attempt: a sign-in that comes back without a usable session would otherwise loop. Keyed by the
// link's state, which is fresh for every sign-in the app starts.
const TRIED_KEY = "appSigninTried";
const triedAlready = (state) => {
  try { return sessionStorage.getItem(TRIED_KEY) === state; } catch (_) { return false; }
};
const markTried = (state) => {
  try { sessionStorage.setItem(TRIED_KEY, state); } catch (_) { /* best effort */ }
};
// Long enough for sign-up's emailed code; see authRedirect.js.
const PHONE_SIGNIN_RETURN_MS = 30 * 60 * 1000;

export default function DeviceConnect() {
  const [params] = useSearchParams();
  const location = useLocation();
  const { instance } = useMsal();
  const isAuth = useIsAuthenticated();
  const app = useMemo(() => parseAppSignin(location.search), [location.search]);
  const direct = Boolean(app?.https); // the iPhone's https return: no Studio screens
  const [directState, setDirectState] = useState("working"); // working | failed
  const started = useRef(false);
  const connected = params.get("connected") === "1";
  const [code, setCode] = useState(normalize(params.get("code")));
  const [session, setSession] = useState("checking"); // checking | ok | expired
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [device, setDevice] = useState("");
  const [error, setError] = useState("");
  const email = accountEmail(instance.getActiveAccount() || instance.getAllAccounts()[0]);

  useEffect(() => { setCode(normalize(params.get("code"))); }, [params]);

  useEffect(() => {
    if (direct) return undefined; // the direct flow checks for itself
    let live = true;
    canCallApiSilently().then((ok) => { if (live) setSession(ok ? "ok" : "expired"); });
    return () => { live = false; };
  }, [direct]);

  // Straight to Microsoft's page (sign-up for create=1), the same request /login makes, coming back
  // to this same link.
  const directSignIn = async () => {
    markTried(app.state);
    savePostAuthRedirect(undefined, { maxAgeMs: PHONE_SIGNIN_RETURN_MS });
    await forgetCachedAccount(instance);
    await instance.loginRedirect({
      ...loginRequest,
      ...(app.create ? { prompt: "create" } : {}),
      extraQueryParameters: { p: b2cPolicyName },
    });
  };

  const sendToApp = (result) => window.location.replace(callbackUrl(app, result));

  useEffect(() => {
    if (!direct || connected || started.current) return;
    started.current = true;
    (async () => {
      // A sign-in already tried for this attempt (or one that just failed on this load) gets a
      // screen with a button instead of another automatic trip.
      const mayRedirect = !triedAlready(app.state) && !authReturnFailed();
      const signInOrStop = async () => {
        if (!mayRedirect) { setDirectState("failed"); return; }
        try { await directSignIn(); } catch (_) { setDirectState("failed"); }
      };
      if (!isAuth || !(await canCallApiSilently())) { await signInOrStop(); return; }
      let res;
      try {
        res = await grantAppSignIn(app.challenge, app.device);
      } catch (err) {
        if (err?.message === "INTERACTION_REQUIRED") { await signInOrStop(); return; }
        res = null;
      }
      try {
        sendToApp(res?.code ? { code: res.code } : { error: "server_error" });
      } catch (_) {
        try { sendToApp({ error: "server_error" }); } catch (__) { setDirectState("failed"); }
      }
    })();
    // Once per page load: the attempt is decided by the link and the session it found.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct, connected]);

  // `another`: the sign-in page itself, not a silent return as whoever Entra still remembers. Clearing
  // MSAL's cache alone isn't enough: Entra's own session signs the same person straight back in.
  const signIn = async (another = false) => {
    savePostAuthRedirect(); // come back to this exact link, code or callback params included
    await forgetCachedAccount(instance);
    await instance.loginRedirect({ ...loginRequest, ...(another ? { prompt: "login" } : {}), extraQueryParameters: { p: b2cPolicyName } });
  };

  const failed = (err, fallback) => {
    if (err?.message === "INTERACTION_REQUIRED") { setSession("expired"); setState("idle"); return; }
    setError(err?.response?.data?.error || err?.message || fallback);
    setState("error");
  };

  const connect = async () => {
    if (!app || state === "busy") return;
    setState("busy"); setError("");
    try {
      const res = await grantAppSignIn(app.challenge, app.device);
      if (!res?.code) throw new Error("Could not connect. Start the sign-in again in Assertion.");
      window.location.replace(callbackUrl(app, { code: res.code }));
    } catch (err) {
      failed(err, "Could not connect. Start the sign-in again in Assertion.");
    }
  };

  const cancel = () => {
    if (app) window.location.replace(callbackUrl(app, { error: "access_denied" }));
  };

  const approve = async (e) => {
    e?.preventDefault();
    if (code.length !== 9 || state === "busy") return;
    setState("busy"); setError("");
    try {
      const res = await approveDevice(code);
      setDevice(res?.device || "");
      setState("done");
    } catch (err) {
      failed(err, "Could not approve that code.");
    }
  };

  const shell = (body) => (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3, bgcolor: "background.default" }}>
      <Paper elevation={0} sx={{ p: 4, maxWidth: 440, width: "100%", border: 1, borderColor: "divider", borderRadius: 3 }}>
        <Box component="img" src={Logo} alt="Assertion" sx={{ height: 28, mb: 3, display: "block" }} />
        {body}
      </Paper>
    </Box>
  );

  if (connected || state === "done") {
    const name = connected ? (params.get("device") || "").slice(0, 120) : device;
    return shell(
      <Box sx={{ textAlign: "center", py: 2 }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Connected</Typography>
        <Typography variant="body2" color="text.secondary">
          {name ? `${name} is signed in to Assertion.` : "Your computer is signed in to Assertion."} You can close this tab and go back to the app.
        </Typography>
        <Button component={RouterLink} to="/account" variant="contained" size="large" sx={{ mt: 2.5, textTransform: "none" }}>
          Open your account
        </Button>
      </Box>
    );
  }

  if (direct) {
    if (directState === "failed") {
      return shell(
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>Sign-in didn't finish</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Try again, or go back to the Assertion app and start over.
          </Typography>
          <Button variant="contained" fullWidth size="large" sx={{ textTransform: "none" }}
                  onClick={() => directSignIn().catch(() => setDirectState("failed"))}>
            Try again
          </Button>
          <Button fullWidth onClick={() => sendToApp({ error: "access_denied" })} sx={{ mt: 1, textTransform: "none" }}>
            Cancel
          </Button>
        </Box>
      );
    }
    return shell(
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 4 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">Signing in…</Typography>
      </Box>
    );
  }

  if (session === "checking") {
    return shell(<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>);
  }

  if (session === "expired") {
    return shell(
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Sign in to connect Assertion</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your Studio session has ended. Sign in again and you'll come straight back here to finish connecting{app?.device ? ` ${app.device}` : " the app"}.
        </Typography>
        <Button variant="contained" fullWidth size="large" onClick={() => signIn()} sx={{ textTransform: "none" }}>
          Sign in
        </Button>
      </Box>
    );
  }

  if (app) {
    return shell(
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Connect Assertion on this computer</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {app.device ? <><b>{app.device}</b> is</> : "The Assertion app or terminal is"} waiting to sign in{email ? <> as <b>{email}</b></> : ""}.
        </Typography>
        {error && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>}
        <Button variant="contained" fullWidth size="large" onClick={connect} disabled={state === "busy"} sx={{ textTransform: "none" }}>
          {state === "busy" ? <CircularProgress size={22} color="inherit" /> : "Connect"}
        </Button>
        <Button fullWidth onClick={cancel} disabled={state === "busy"} sx={{ mt: 1, textTransform: "none" }}>
          Cancel
        </Button>
        {email && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Not {email}? <Box component="button" type="button" onClick={() => signIn(true)}
              sx={{ p: 0, border: 0, background: "none", color: "primary.main", cursor: "pointer", font: "inherit", textDecoration: "underline" }}>
              Sign in with another account
            </Box>
          </Typography>
        )}
      </Box>
    );
  }

  return shell(
    <Box component="form" onSubmit={approve}>
      <Typography variant="h6" sx={{ mb: 1 }}>Connect Assertion on your computer</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Assertion is waiting to sign in{email ? <> as <b>{email}</b></> : " as this account"}. Confirm the code it shows you, then approve.
      </Typography>
      <TextField
        label="Code shown in Assertion"
        value={code}
        onChange={(e) => { setCode(normalize(e.target.value)); setError(""); if (state === "error") setState("idle"); }}
        placeholder="XXXX-XXXX"
        fullWidth
        autoFocus={!code}
        error={Boolean(error)}
        helperText={error || " "}
        inputProps={{ style: { fontFamily: "monospace", letterSpacing: "0.12em", fontSize: 20, textAlign: "center" }, spellCheck: false, autoComplete: "off" }}
        sx={{ mb: 2 }}
      />
      <Button type="submit" variant="contained" fullWidth size="large" disabled={code.length !== 9 || state === "busy"}>
        {state === "busy" ? <CircularProgress size={22} color="inherit" /> : "Approve sign-in"}
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Only approve a code you are looking at in Assertion right now. Codes expire after ten minutes.
      </Typography>
    </Box>
  );
}
